import { useCallback, useEffect, useState } from "react";
import { PackageOpen } from "lucide-react";
import { api } from "@/lib/api";
import { fdate, mxn } from "@/lib/format";
import { bienTexto } from "./OtEstado";

// «Bienes devueltos» de Cartera: aplica solo si la entidad tiene arrendamiento habilitado.
export function useBienesDevueltos() {
  const [st, setSt] = useState({ aplica: false, bienes: [] });
  const recargar = useCallback(async () => {
    try { setSt((await api.get("/arrendamiento/bienes-devueltos")).data); } catch { setSt({ aplica: false, bienes: [] }); }
  }, []);
  useEffect(() => { recargar(); }, [recargar]);
  return { ...st, recargar };
}

const COLS = ["Crédito", "Arrendatario", "Bien", "Residual cancelado", "Saldo 1151", "Entrega", "Recibió", "Estado del bien", "Folio"];

// Build 4 · pie con el total de 1151 «Bienes recuperados de arrendamiento» (pólizas de devolución).
function TotalCuentaBienes({ cuenta }) {
  if (!cuenta) return null;
  return (
    <tfoot className="border-t-2 border-slate-200 bg-slate-50" data-testid="bienes-devueltos-saldo-cuenta">
      <tr>
        <td colSpan={4} className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">
          Total <span className="font-mono normal-case text-slate-500">{cuenta.codigo}</span> · {cuenta.nombre || "Bienes recuperados de arrendamiento"}
        </td>
        <td className="px-3 py-2 text-right font-mono text-sm font-semibold tabular-nums text-slate-900" data-testid="bienes-devueltos-saldo">
          {mxn(cuenta.saldo)}
        </td>
        <td colSpan={COLS.length - 5} />
      </tr>
    </tfoot>
  );
}

export function BienesDevueltos({ bienes, cuenta, onAbrir }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="bienes-devueltos">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
        <PackageOpen className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-sm font-semibold text-slate-800">Bienes devueltos ({bienes.length})</h3>
        <span className="text-xs text-slate-400">· arrendamientos cerrados por devolución del bien</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>{COLS.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}</tr>
          </thead>
          <tbody>
            {bienes.length === 0 && <tr><td colSpan={COLS.length} className="px-3 py-6 text-center text-slate-400">Sin bienes devueltos</td></tr>}
            {bienes.map((b) => (
              <tr key={b.credito_id} onClick={() => onAbrir(b.credito_id)} data-testid={`bien-devuelto-${b.credito_id}`}
                className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs">{b.numero_credito}</td>
                <td className="px-3 py-2">{b.cliente}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{bienTexto(b)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{mxn(b.valor_residual)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums" data-testid={`bien-devuelto-saldo-${b.credito_id}`}>
                  {mxn(b.saldo_1151)}
                </td>
                <td className="px-3 py-2 text-slate-500">{fdate(b.fecha_entrega)}</td>
                <td className="px-3 py-2">{b.recibido_por}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{b.estado_bien || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{b.folio}</td>
              </tr>
            ))}
          </tbody>
          <TotalCuentaBienes cuenta={cuenta} />
        </table>
      </div>
    </div>
  );
}
